'use client';

import { c as _c } from "react/compiler-runtime";
import { getCaretCoordinates } from '@shared/utils/helpers/getCaretCoordinates';
import useTagSuggestion from '@shared/utils/hooks/useTagSuggestion';
import { useIsFetching } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useLocationSearch } from '../../hooks/useLocationSearchParam';
import { decodeScope, encodeScope, useSearchScope } from '../../hooks/useSearchScope';
import { type PhotonSearchResult } from '../../services/photonSearch';
import { spaceConfig } from '../../utils/getSpaceConfig';
import TagSuggestionModal from '../posts/TagSuggestionModal';
import SearchDropdown from './SearchDropdown';
type Props = {
  className?: string;
};
const SearchBar = t0 => {
  const $ = _c(76);
  const {
    className
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const query = new URLSearchParams(useLocationSearch() ?? "");
  const searchBarInputRef = useRef(null);
  const [content, setContent] = useState(query.get("q") ?? "");
  const [mounted, setMounted] = useState(false);
  const {
    scope: registeredScope,
    query: scopedQuery,
    dispatch
  } = useSearchScope();
  const isMapPage = pathname === "/map";
  const isSearchPage = pathname === "/search";
  const urlScope = decodeScope(query.get("scope"));
  const scope = isSearchPage ? urlScope : registeredScope;
  const isBoardScope = scope?.kind === "board";
  const shouldNavigateToSearch = !isMapPage && !isSearchPage && !scope;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["semanticSearch"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const semanticFetching = useIsFetching(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["boardSearch"]
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const boardFetching = useIsFetching(t2);
  const isSearching = (semanticFetching > 0 || boardFetching > 0) && (isSearchPage || Boolean(scope)) && mounted;
  const value = isBoardScope ? scopedQuery : content;
  const syncUrl = next => {
    const params = new URLSearchParams(isSearchPage ? window.location.search : "");
    const queryWords = extractQuery(next);
    if (queryWords.length > 0) {
      params.set("q", queryWords.join(" "));
    } else {
      params.delete("q");
    }
    const encoded = scope ? encodeScope(scope) : null;
    if (encoded) {
      params.set("scope", encoded);
    } else {
      params.delete("scope");
    }
    const url = `/search?${params.toString()}`;
    if (isSearchPage) {
      router.replace(url, {
        scroll: false
      });
    } else {
      router.push(url, {
        scroll: false
      });
    }
  };
  const setValue = next_0 => {
    if (isBoardScope) {
      dispatch({
        type: "query",
        query: next_0
      });
      return;
    }
    setContent(next_0);
    if (isSearchPage || scope) {
      syncUrl(next_0);
    }
  };
  let t3;
  if ($[2] !== dispatch || $[3] !== isSearchPage || $[4] !== router) {
    t3 = () => {
      if (isSearchPage) {
        const params_0 = new URLSearchParams(window.location.search);
        params_0.delete("scope");
        router.replace(`/search?${params_0.toString()}`, {
          scroll: false
        });
        return;
      }
      dispatch({
        type: "dismiss"
      });
    };
    $[2] = dispatch;
    $[3] = isSearchPage;
    $[4] = router;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const dismissScope = t3;
  let t4;
  if ($[6] !== setContent || $[7] !== setMounted) {
    t4 = () => {
      setMounted(true);
      const q = new URLSearchParams(window.location.search).get("q");
      if (q) {
        setContent(q);
      }
    };
    $[6] = setContent;
    $[7] = setMounted;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  const t6 = isMapPage || scope ? "" : content;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = async () => {
      const input = searchBarInputRef.current;
      if (!input) {
        return {
          top: 0,
          left: 0
        };
      }
      const coords = getCaretCoordinates(input);
      return {
        top: coords.top + coords.height,
        left: coords.left
      };
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== router) {
    t8 = (type, _id, slug) => {
      const routeMap = {
        user: `/user/${slug}`,
        space: `/s/${slug}`,
        factiii: `/f/${slug}`
      } as const;
      router.push(routeMap[type]);
    };
    $[11] = router;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== setContent || $[14] !== t6 || $[15] !== t8) {
    t9 = {
      content: t6,
      setContent,
      inputRef: searchBarInputRef,
      TagSuggestionModal,
      resetSearchBar: true,
      getCaretPosition: t7,
      onItemClick: t8
    };
    $[13] = setContent;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    renderSuggestions
  } = useTagSuggestion(t9);
  let t10;
  let t11;
  if ($[17] !== router || $[18] !== shouldNavigateToSearch) {
    t10 = () => {
      const handleKeyDown = function handleKeyDown(event) {
        if ((event.ctrlKey || event.metaKey) && event.key === "k") {
          event.preventDefault();
          if (shouldNavigateToSearch) {
            router.push("/search");
          } else {
            searchBarInputRef.current?.focus();
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t11 = [shouldNavigateToSearch, router];
    $[17] = router;
    $[18] = shouldNavigateToSearch;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  const searchHandler = e => {
    e.preventDefault();
    if (isBoardScope) {
      return;
    }
    if (!content) {
      return;
    }
    if (isMapPage) {
      return;
    }
    if (content.toLowerCase().includes("map") || content.toLowerCase().includes("location")) {
      router.push("/map");
      return;
    }
    return syncUrl(content);
  };
  let t12;
  if ($[21] !== router || $[22] !== setContent) {
    t12 = result => {
      const [longitude, latitude] = result.geometry.coordinates;
      setContent("");
      router.push(`/map?latitude=${latitude}&longitude=${longitude}`);
    };
    $[21] = router;
    $[22] = setContent;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleSearchResultClick = t12;
  const extractQuery = _temp;
  let t13;
  if ($[24] !== isBoardScope || $[25] !== isMapPage || $[26] !== isSearchPage) {
    t13 = () => {
      if (isMapPage) {
        return "Search map";
      }
      if (isBoardScope) {
        return "Search cards";
      }
      if (isSearchPage) {
        return `Search ${spaceConfig.space.slug}...`;
      }
      return `Search ${spaceConfig.space.slug}`;
    };
    $[24] = isBoardScope;
    $[25] = isMapPage;
    $[26] = isSearchPage;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const getPlaceholder = t13;
  if (shouldNavigateToSearch) {
    const t14 = `relative flex h-11 w-full min-w-0 items-center overflow-hidden rounded-full border border-solid border-ds-border-standard bg-background-secondary px-2.5 py-0 align-middle no-underline transition-all duration-200 hover:border-ds-border-hover ${className || ""}`;
    let t15;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <div className="shrink-0 text-textSecondary transition-transform duration-200 hover:scale-110"><Search height={17} width={17} /></div>;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    const t16 = getPlaceholder();
    let t17;
    if ($[29] !== t16) {
      t17 = <div className="ml-2 flex w-full min-w-0 items-center justify-between">{t15}<span className="ml-2 w-0 grow truncate text-textSecondary">{t16}</span></div>;
      $[29] = t16;
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    let t18;
    if ($[31] !== t14 || $[32] !== t17) {
      t18 = <Link href="/search" title="Search" className={t14}>{t17}</Link>;
      $[31] = t14;
      $[32] = t17;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    return t18;
  }
  const t14 = `relative flex h-11 w-full min-w-0 rounded-full border border-solid border-ds-border-standard bg-background-secondary px-2.5 py-0 align-middle transition-all duration-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 hover:border-ds-border-hover ${className || ""}`;
  const t15 = `transition-all duration-200 ${isSearching ? "scale-50 opacity-0" : "scale-100 opacity-100"}`;
  let t16;
  if ($[34] !== t15) {
    t16 = <Search height={17} width={17} className={t15} />;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = `absolute inset-0 animate-spin transition-all duration-200 ${isSearching ? "scale-100 opacity-100" : "scale-50 opacity-0"}`;
  let t18;
  if ($[36] !== t17) {
    t18 = <Loader2 height={17} width={17} className={t17} />;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t16 || $[39] !== t18) {
    t19 = <div className="relative shrink-0 text-textSecondary transition-transform duration-200 hover:scale-110">{t16}{t18}</div>;
    $[38] = t16;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== dismissScope || $[42] !== scope) {
    t20 = scope && <span data-id="search-scope-pill" className="ml-2 flex h-7 min-w-0 max-w-[50%] items-center gap-1 rounded-full bg-primary/15 py-0 pl-2.5 pr-1 text-sm font-emphasis text-primary ring-1 ring-inset ring-primary/25"><span className="min-w-0 truncate">{scope.label}</span><button type="button" onClick={dismissScope} aria-label={`Search all of ${spaceConfig.space.slug} instead of ${scope.label}`} title="Search the whole site" className="relative shrink-0 rounded-full p-0.5 text-primary/70 transition-colors after:absolute after:-inset-3 after:content-[''] hover:bg-primary/20 hover:text-primary"><X height={13} width={13} /></button></span>;
    $[41] = dismissScope;
    $[42] = scope;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== setValue || $[45] !== value) {
    t21 = e_0 => {
      if (e_0.key === "Escape" && value) {
        setValue("");
      }
    };
    $[44] = setValue;
    $[45] = value;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== setValue) {
    t22 = e_1 => setValue(e_1.target.value);
    $[47] = setValue;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const t23 = getPlaceholder();
  let t24;
  if ($[49] !== isSearchPage || $[50] !== t21 || $[51] !== t22 || $[52] !== t23 || $[53] !== value) {
    t24 = <input type="text" value={value} onKeyDown={t21} onChange={t22} placeholder={t23} className="ml-2 h-full w-0 min-w-0 grow bg-transparent text-textPrimary outline-none placeholder:text-textSecondary" ref={searchBarInputRef} autoFocus={isSearchPage} />;
    $[49] = isSearchPage;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = value;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== setValue || $[56] !== value.length) {
    t25 = value.length > 0 && <button type="button" onClick={() => {
      setValue("");
      searchBarInputRef.current?.focus();
    }} aria-label="Clear search" className="relative mr-1 shrink-0 rounded-full p-1 text-textSecondary transition-colors after:absolute after:-inset-2.5 after:content-[''] hover:text-textPrimary"><X height={16} width={16} /></button>;
    $[55] = setValue;
    $[56] = value.length;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t19 || $[59] !== t20 || $[60] !== t24 || $[61] !== t25) {
    t26 = <div className="ml-2 flex w-full min-w-0 items-center justify-between">{t19}{t20}{t24}{t25}</div>;
    $[58] = t19;
    $[59] = t20;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== isSearchPage || $[64] !== renderSuggestions) {
    t27 = renderSuggestions({
      fullWidth: isSearchPage
    });
    $[63] = isSearchPage;
    $[64] = renderSuggestions;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== content || $[67] !== handleSearchResultClick || $[68] !== isMapPage) {
    t28 = isMapPage && <SearchDropdown query={content} onResultClick={handleSearchResultClick} />;
    $[66] = content;
    $[67] = handleSearchResultClick;
    $[68] = isMapPage;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== searchHandler || $[71] !== t14 || $[72] !== t26 || $[73] !== t27 || $[74] !== t28) {
    t29 = <form data-id="search-bar" className={t14} onSubmit={searchHandler}>{t26}{t27}{t28}</form>;
    $[70] = searchHandler;
    $[71] = t14;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  return t29;
};
export default SearchBar;
function _temp(content_0) {
  const words = [];
  content_0.trim().split(/\s+/).forEach(word => {
    if (word && !(word.startsWith("@") || word.startsWith("s/") || word.startsWith("f/"))) {
      words.push(word);
    }
  });
  return words;
}